import { Controller } from "@hotwired/stimulus"
import IMask from "imask"

export default class extends Controller {
  static values = {
    mask: String,
    pattern: String,
    min: Number,
    max: Number,
    scale: Number,
    radix: String,
    thousandsSeparator: String,
    lazy: { type: Boolean, default: true },
    overwrite: { type: Boolean, default: true }
  }

  connect() {
    this.initMask()
    this.bindFormSubmit()
  }

  disconnect() {
    if (this.mask) {
      this.mask.destroy()
    }
    this.unbindFormSubmit()
  }

  bindFormSubmit() {
    this.form = this.element.closest("form")
    if (this.form) {
      this.submitHandler = this.handleSubmit.bind(this)
      this.form.addEventListener("submit", this.submitHandler)
    }
  }

  unbindFormSubmit() {
    if (this.form && this.submitHandler) {
      this.form.removeEventListener("submit", this.submitHandler)
    }
  }

  handleSubmit() {
    // Replace the formatted value with the unmasked value before form submission
    if (this.mask) {
      this.element.value = this.mask.unmaskedValue

      // And put the formatting straight back. The form data is taken the moment
      // this returns, so the submission loses nothing by it - while a submission
      // that never happens, because a „Ertu viss?" was declined, would otherwise
      // leave the ref looking at 20000 where they typed 20.000.
      setTimeout(() => { if (this.mask) this.element.value = this.mask.value }, 0)
    }
  }

  initMask() {
    const options = this.buildOptions()
    this.mask = IMask(this.element, options)

    // Said once the mask has settled on a keystroke, so a listener reads the
    // value the mask kept rather than racing it for the input event. Bound
    // after the mask is made, so the value it starts with is not news.
    this.mask.on("accept", () => this.dispatch("accept"))
  }

  buildOptions() {
    const maskType = this.maskValue

    switch (maskType) {
      case "number":
        return this.numberOptions()
      case "phone":
        return this.phoneOptions()
      case "ssn":
        return this.ssnOptions()
      case "date":
        return this.dateOptions()
      case "time":
        return this.timeOptions()
      case "currency":
        return this.currencyOptions()
      default:
        // Custom pattern mask
        if (this.patternValue) {
          return { mask: this.patternValue }
        }
        // Default to the mask value as a pattern
        return { mask: maskType || /.*/ }
    }
  }

  numberOptions() {
    return {
      mask: Number,
      min: this.hasMinValue ? this.minValue : undefined,
      max: this.hasMaxValue ? this.maxValue : undefined,
      scale: this.hasScaleValue ? this.scaleValue : 0,
      radix: this.radixValue || ",",
      thousandsSeparator: this.thousandsSeparatorValue || ".",
      lazy: this.lazyValue,
      overwrite: this.overwriteValue
    }
  }

  phoneOptions() {
    return {
      mask: "+000 000 0000",
      lazy: this.lazyValue
    }
  }

  ssnOptions() {
    // Icelandic kennitala format: DDMMYY-XXXX
    return {
      mask: "000000-0000",
      lazy: this.lazyValue
    }
  }

  dateOptions() {
    return {
      mask: Date,
      pattern: "d.m.Y",
      lazy: this.lazyValue,
      format: function (date) {
        let day = date.getDate()
        let month = date.getMonth() + 1
        let year = date.getFullYear()

        if (day < 10) day = "0" + day
        if (month < 10) month = "0" + month

        return [day, month, year].join(".")
      },
      parse: function (str) {
        const [day, month, year] = str.split(".")
        return new Date(year, month - 1, day)
      },
      blocks: {
        d: { mask: IMask.MaskedRange, from: 1, to: 31, maxLength: 2 },
        m: { mask: IMask.MaskedRange, from: 1, to: 12, maxLength: 2 },
        Y: { mask: IMask.MaskedRange, from: 1900, to: 2100 }
      }
    }
  }

  timeOptions() {
    return {
      mask: "HH:MM",
      lazy: this.lazyValue,
      blocks: {
        HH: { mask: IMask.MaskedRange, from: 0, to: 23, maxLength: 2 },
        MM: { mask: IMask.MaskedRange, from: 0, to: 59, maxLength: 2 }
      }
    }
  }

  currencyOptions() {
    return {
      mask: Number,
      scale: this.hasScaleValue ? this.scaleValue : 0,
      radix: this.radixValue || ",",
      thousandsSeparator: this.thousandsSeparatorValue || ".",
      min: this.hasMinValue ? this.minValue : 0,
      max: this.hasMaxValue ? this.maxValue : undefined,
      lazy: this.lazyValue,
      overwrite: this.overwriteValue
    }
  }
}
